import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  CheckCircle, Search, RefreshCw, History, ArrowLeft, ArrowRight
} from 'lucide-react';
import api, { formatErrorMessage } from '../../../../utils/api';
import { toast } from 'sonner';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const fmtCLP = (n) => new Intl.NumberFormat('es-CL').format(Math.round(Number(n || 0)));

/**
 * Pestaña "Pagadas" con búsqueda server-side (número factura, nombre cliente o RUT
 * incluyendo rut_normalizado) y paginación real (no depende de un tope de 1000).
 * Mantiene el detalle por factura: cada fila corresponde a una factura individual
 * con su client_id de origen preservado.
 */
const PagadasConsolidado = ({ onVerHistorial }) => {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ total: 0, facturas: [], totales: {}, total_pages: 0 });

  // Debounce búsqueda 400ms
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 400);
    return () => clearTimeout(id);
  }, [q]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (debouncedQ) params.set('q', debouncedQ);
      const r = await api.get(`/finanzas/cobranza/pagadas?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      toast.error(formatErrorMessage(err) || 'Error al cargar facturas pagadas');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedQ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page cuando cambia búsqueda o tamaño
  useEffect(() => { setPage(1); }, [debouncedQ, pageSize]);

  const totales = data.totales || {};
  const facturas = data.facturas || [];
  const totalPages = data.total_pages || 1;

  return (
    <div className='space-y-4' data-testid='pagadas-consolidado'>
      {/* Resumen */}
      <Card className='p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'>
        <div className='flex gap-6 flex-wrap'>
          <div className='flex items-center gap-2'>
            <div className='w-3 h-3 rounded-full bg-green-500' />
            <span className='text-sm text-slate-600'>
              Total Recaudado: <strong className='text-green-700'>${fmtCLP(totales.total_recaudado_real_clp)}</strong>
            </span>
          </div>
          {(totales.total_notas_credito_clp || 0) > 0 && (
            <div className='flex items-center gap-2'>
              <div className='w-3 h-3 rounded-full bg-blue-500' />
              <span className='text-sm text-slate-600'>
                NC Emitidas: <strong className='text-blue-700'>${fmtCLP(totales.total_notas_credito_clp)}</strong>
              </span>
            </div>
          )}
          {(totales.total_notas_debito_clp || 0) > 0 && (
            <div className='flex items-center gap-2'>
              <div className='w-3 h-3 rounded-full bg-amber-500' />
              <span className='text-sm text-slate-600'>
                ND Emitidas: <strong className='text-amber-700'>${fmtCLP(totales.total_notas_debito_clp)}</strong>
              </span>
            </div>
          )}
          <div className='flex items-center gap-2'>
            <CheckCircle className='w-4 h-4 text-green-600' />
            <span className='text-sm text-slate-600'>
              <strong>{totales.cantidad || 0}</strong> facturas cerradas
              {(totales.cantidad_cerradas_con_nc || 0) > 0 && (
                <span className='text-xs text-slate-500 ml-1'>
                  ({totales.cantidad_cobradas_100 || 0} cobradas, {totales.cantidad_cerradas_con_nc || 0} con NC,{' '}
                  {totales.cantidad_cerradas_solo_nc || 0} solo NC)
                </span>
              )}
            </span>
          </div>
        </div>
      </Card>

      {/* Controles: búsqueda + tamaño página */}
      <div className='flex items-center gap-3 flex-wrap'>
        <div className='relative flex-1 max-w-md'>
          <Search className='w-4 h-4 text-slate-400 absolute left-2.5 top-2.5' />
          <Input
            type='text'
            placeholder='Buscar por N° factura, cliente o RUT (con o sin puntos)...'
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className='pl-8 h-9 text-sm'
            data-testid='pagadas-search-input'
          />
        </div>

        <div className='flex items-center gap-1 text-xs text-slate-600'>
          <span>Filas:</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className='w-20 h-8 text-xs' data-testid='pagadas-page-size-select'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map(n => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          size='sm'
          variant='outline'
          className='h-9 text-xs ml-auto'
          onClick={fetchData}
          data-testid='pagadas-refresh-btn'
        >
          <RefreshCw className='w-3 h-3 mr-1' /> Actualizar
        </Button>
      </div>

      {/* Tabla */}
      <Card className='border-slate-200'>
        <div className='max-h-[500px] overflow-y-auto'>
          {loading ? (
            <div className='p-12 text-center'>
              <div className='animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2' />
              <p className='text-slate-600'>Cargando facturas...</p>
            </div>
          ) : facturas.length === 0 ? (
            <div className='p-12 text-center'>
              <CheckCircle className='w-12 h-12 text-slate-300 mx-auto mb-3' />
              <p className='text-slate-600 font-medium'>
                {debouncedQ ? 'No se encontraron facturas para la búsqueda' : 'No hay facturas pagadas'}
              </p>
              {!debouncedQ && (
                <p className='text-sm text-slate-400'>Las facturas cobradas aparecerán aquí</p>
              )}
            </div>
          ) : (
            <table className='w-full text-sm'>
              <thead className='bg-green-50 sticky top-0 z-10'>
                <tr>
                  <th className='text-left p-3 font-semibold text-slate-600 border-b'>N° Factura</th>
                  <th className='text-left p-3 font-semibold text-slate-600 border-b'>Cliente</th>
                  <th className='text-left p-3 font-semibold text-slate-600 border-b'>RUT</th>
                  <th className='text-right p-3 font-semibold text-slate-600 border-b'>Desglose</th>
                  <th className='text-center p-3 font-semibold text-slate-600 border-b'>Fecha Cierre</th>
                  <th className='text-center p-3 font-semibold text-slate-600 border-b'>Estado</th>
                  <th className='text-center p-3 font-semibold text-slate-600 border-b'>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {facturas.map((f) => (
                  <tr key={f.id} className='border-b border-slate-100 hover:bg-green-50/50 transition-colors'
                      data-testid={`pagada-row-${f.id}`}>
                    <td className='p-3'>
                      <p className='font-bold text-slate-900'>#{f.numero_factura}</p>
                      <p className='text-xs text-slate-500'>
                        Emitida: {f.fecha_emision ? new Date(f.fecha_emision).toLocaleDateString('es-CL') : 'N/A'}
                      </p>
                      <p className='text-[10px] text-slate-400 font-mono'>cid: {f.client_id?.slice(0, 8)}</p>
                    </td>
                    <td className='p-3'>
                      <p className='font-medium text-slate-900'>{f.cliente?.company || f.cliente?.name || '—'}</p>
                    </td>
                    <td className='p-3 font-mono text-xs'>{f.cliente?.rut || '—'}</td>
                    <td className='p-3 text-right'>
                      <p className='text-xs text-slate-500 mb-1'>Factura: ${fmtCLP(f.total_amount_clp)}</p>
                      {f.paid_amount_clp > 0 && (
                        <p className='text-sm font-semibold text-green-700'>Pagado: ${fmtCLP(f.paid_amount_clp)}</p>
                      )}
                      {f.total_credit_notes_clp > 0 && (
                        <p className='text-sm text-blue-600'>NC: ${fmtCLP(f.total_credit_notes_clp)}</p>
                      )}
                      {f.total_debit_notes_clp > 0 && (
                        <p className='text-sm text-amber-600'>ND: +${fmtCLP(f.total_debit_notes_clp)}</p>
                      )}
                    </td>
                    <td className='p-3 text-center'>
                      <p className='font-medium text-sm text-slate-900'>
                        {f.fecha_pago ? new Date(f.fecha_pago).toLocaleDateString('es-CL') : '—'}
                      </p>
                    </td>
                    <td className='p-3 text-center'>
                      {f.closing_type === 'paid_full' ? (
                        <Badge className='bg-green-100 text-green-700 border-green-200'>Cobrada</Badge>
                      ) : f.closing_type === 'mixed' ? (
                        <Badge className='bg-blue-100 text-blue-700 border-blue-200'>Pago + NC</Badge>
                      ) : f.closing_type === 'credit_note_only' ? (
                        <Badge className='bg-purple-100 text-purple-700 border-purple-200'>Cerrada NC</Badge>
                      ) : (
                        <Badge className='bg-slate-100 text-slate-700 border-slate-200'>Cerrada</Badge>
                      )}
                    </td>
                    <td className='p-3 text-center'>
                      <Button
                        size='sm'
                        variant='outline'
                        className='h-7 text-xs'
                        onClick={() => onVerHistorial?.(f)}
                        data-testid={`ver-historial-btn-${f.id}`}
                      >
                        <History className='w-3 h-3 mr-1' /> Historial
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className='flex items-center justify-center gap-2 py-2'>
          <Button size='sm' variant='outline' className='h-7' disabled={page <= 1}
                  onClick={() => setPage(page - 1)} data-testid='pagadas-pag-prev'>
            <ArrowLeft className='w-3 h-3' />
          </Button>
          <span className='text-xs text-slate-600'>
            Página {page} de {totalPages} · {data.total} registros
          </span>
          <Button size='sm' variant='outline' className='h-7' disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)} data-testid='pagadas-pag-next'>
            <ArrowRight className='w-3 h-3' />
          </Button>
        </div>
      )}
      {totalPages <= 1 && data.total !== undefined && (
        <p className='text-xs text-slate-500 text-center'>{data.total} registros</p>
      )}
    </div>
  );
};

export default PagadasConsolidado;
